import { KeyIcon, PlusIcon, ProhibitIcon, PulseIcon, XIcon } from "@phosphor-icons/react";
import { AgentAssist } from "./agent-assist.tsx";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import type * as C from "@zooi/contracts";
import { type FormEvent, useState } from "react";
import { api, q } from "./api.ts";
import { useMetricActions } from "./metric-actions-context.ts";
import { dateTime, number, relative } from "./format.ts";
import { EVENT_INTAKE_LABELS as L } from "./labels.ts";
import { Button, Callout, CopyButton, ErrorText, Field, Modal, Section } from "./ui.tsx";
import { ListRow } from "./ui/list.tsx";
import { PropertyList, PropertyRow } from "./ui/props.tsx";
import { Skeleton } from "./ui/skeleton.tsx";
import { Chip } from "./ui/status.tsx";

// Insights › Data sources › Events from your product (plan M6 step 6d;
// plugin/spec/events.md; docs/design-system.md §0.3d): where the product's
// backend sends `zooi.event/1`, each event name with its counts, and the
// write-only keys it uses, one row each; a key is created on demand. An event
// becomes a metric when the person collects it. A person's session only.

/** A new key's secret stays in this component, never in query or mutation caches. */
function IssuedKey({ issued, onDismiss }: { issued: C.IssuedIngestKey; onDismiss: () => void }) {
  return <Callout><div className="z-flow">
    <p className="z-row-title">{L.secretTitle}</p>
    <p>{L.secretNote}</p>
    <Field label={L.secret}><input className="z-input z-mono" type="password" aria-label={L.secret} value={issued.secret} readOnly autoComplete="off" spellCheck={false} /></Field>
    <div className="z-inline"><CopyButton text={issued.secret} label={L.copySecret} /><Button size="sm" onClick={onDismiss}>{L.done}</Button></div>
  </div></Callout>;
}

const example = (endpoint: string) => [
  `curl -X POST ${endpoint} \\`,
  `  -H "Authorization: Bearer $ZOOI_INGEST_KEY" \\`,
  `  -H "Content-Type: application/json" \\`,
  `  -d '{"schema":"zooi.event/1","events":[{"name":"signup","id":"<your id>","occurred_at":"2026-10-19T08:00:00Z","source":{"utm_source":"hn"}}]}'`,
].join("\n");

export function EventIntake({ product }: { product: C.Product }) {
  const actions = useMetricActions();
  const client = useQueryClient();
  const intake = useQuery(q.eventIntake(product.id));
  const keys = useQuery(q.ingestKeys(product.id));
  const [name, setName] = useState("");
  const [creating, setCreating] = useState(false);
  const [issued, setIssued] = useState<C.IssuedIngestKey | null>(null);
  const [revoking, setRevoking] = useState<C.IngestKey | null>(null);
  const refresh = () => Promise.all([
    client.invalidateQueries({ queryKey: q.ingestKeys(product.id).queryKey }),
    client.invalidateQueries({ queryKey: q.eventIntake(product.id).queryKey }),
    client.invalidateQueries({ queryKey: q.metricCollectors(product.id).queryKey }),
  ]);
  const create = useMutation({
    mutationFn: async () => {
      const data = await api.post<C.IssuedIngestKey>(`/api/products/${encodeURIComponent(product.id)}/ingest-keys`, { name });
      setIssued(data);
      return data.key;
    },
    onSuccess: async () => { setName(""); setCreating(false); await refresh(); },
  });
  const revoke = useMutation({
    mutationFn: (id: string) => api.del<C.IngestKey>(`/api/ingest-keys/${encodeURIComponent(id)}`),
    onSuccess: async () => { setRevoking(null); await refresh(); },
  });
  const collect = useMutation({
    mutationFn: (event: string) => api.post<C.MetricCollector>(`/api/products/${encodeURIComponent(product.id)}/metric-collectors`, { collector: "event_count", subject: event }),
    onSuccess: refresh,
  });
  const submit = (event: FormEvent) => { event.preventDefault(); create.mutate(); };
  const endpoint = intake.data?.endpoint ?? `${window.location.origin}/api/events`;

  const events = intake.data?.events ?? [];
  const activeKeys = (keys.data ?? []).filter((key) => !key.revoked_at);
  return <Section title={L.title} actions={<Button size="sm" tone="ghost" icon={creating ? <XIcon /> : <PlusIcon />} aria-expanded={creating} disabled={issued !== null} onClick={() => setCreating(!creating)}>{creating ? L.cancel : L.create}</Button>}>
    {issued && <Modal open onOpenChange={next => { if (!next) setIssued(null); }} title={L.secretTitle}><IssuedKey key={issued.key.id} issued={issued} onDismiss={() => setIssued(null)} /></Modal>}
    {creating && <form className="z-edit-block z-flow" onSubmit={submit}>
      <Field label={L.name} help={L.nameHelp}><input className="z-input" aria-label={L.name} placeholder={L.namePlaceholder} value={name} onChange={(event) => setName(event.target.value)} maxLength={80} required disabled={create.isPending} autoFocus /></Field>
      <div className="z-inline"><Button tone="primary" type="submit" busy={create.isPending} disabled={issued !== null}>{L.create}</Button><Button onClick={() => setCreating(false)}>{L.cancel}</Button></div>
      <ErrorText error={create.error} />
    </form>}
    <PropertyList>
      <PropertyRow label={L.endpoint}><span className="z-mono">POST {endpoint}</span><CopyButton text={endpoint} label={L.copyEndpoint} /></PropertyRow>
      <PropertyRow label={L.lastReceived}>{intake.data?.last_received_at ? <>{relative(intake.data.last_received_at)} · <span className="z-muted">{dateTime(intake.data.last_received_at, product.settings.timezone)}</span></> : L.never}</PropertyRow>
      <PropertyRow label={L.keys}>{keys.isSuccess && (activeKeys.length ? L.activeKeys(activeKeys.length) : L.noKeys)}</PropertyRow>
    </PropertyList>

    {intake.isLoading ? <Skeleton shape="list" rows={2} /> : events.length ? <>
      <div className="z-boxed" aria-label={L.events} role="group">
        {events.map((event) => <ListRow key={event.name} icon={<PulseIcon size={16} />} title={event.name}
          sub={[L.today(number(event.today)), L.lastSevenDays(number(event.last_7_days))].join(" · ")}
          meta={L.lastArrived(relative(event.last_received_at))}
          end={event.metric_id
            ? <button type="button" className="z-link z-linkbtn z-small" onClick={() => actions.open(event.metric_id!)}>{L.openMetric}</button>
            : <Button size="sm" busy={collect.isPending && collect.variables === event.name} onClick={() => collect.mutate(event.name)}>{L.collect}</Button>} />)}
      </div>
      <p className="z-small z-muted">{L.collectNote}</p>
    </> : intake.isSuccess && <p className="z-small z-muted">{L.note} {L.noEvents}</p>}
    <ErrorText error={intake.error ?? collect.error ?? keys.error} />

    {(keys.data?.length ?? 0) > 0 && <div className="z-boxed" aria-label={L.keys} role="group">
      {keys.data!.map((key) => <ListRow key={key.id} icon={<KeyIcon size={16} />} title={key.name}
        sub={[`…${key.hint}`, L.created(dateTime(key.created_at)), key.last_used_at ? L.lastUsed(relative(key.last_used_at)) : L.neverUsed].join(" · ")}
        chips={key.revoked_at && <Chip>{L.revoked}</Chip>}
        end={!key.revoked_at && <Button size="sm" tone="ghost" icon={<ProhibitIcon />} className="z-reveal" onClick={() => { revoke.reset(); setRevoking(key); }}>{L.revoke}</Button>} />)}
    </div>}
    <AgentAssist productId={product.id} task="events" folded />
    <details className="z-disclosure">
      <summary>{L.how}</summary>
      <p className="z-small z-muted">{L.howNote}</p>
      <pre className="z-mono z-code">{example(endpoint)}</pre>
      <CopyButton text={example(endpoint)} />
    </details>

    <Modal open={revoking !== null} onOpenChange={(open) => { if (!open && !revoke.isPending) setRevoking(null); }} title={L.revokeTitle(revoking?.name ?? "")} footer={<>
      <Button disabled={revoke.isPending} onClick={() => setRevoking(null)}>{L.cancel}</Button>
      <Button tone="danger" busy={revoke.isPending} onClick={() => { if (revoking) revoke.mutate(revoking.id); }}>{L.revoke}</Button>
    </>}><p>{L.revokeNote}</p><ErrorText error={revoke.error} /></Modal>
  </Section>;
}
